/**
 * SignalR Real-Time Hook — Connects to Azure SignalR for live SOC updates
 *
 * Replaces polling with WebSocket push for alerts, incidents, and dashboard refreshes.
 * Falls back gracefully if SignalR is unavailable (polling continues as before).
 */

import { useEffect, useRef, useCallback } from 'react';
import { HubConnectionBuilder, HubConnectionState, LogLevel } from '@microsoft/signalr';
import type { HubConnection } from '@microsoft/signalr';
import { secopsApi } from '../lib/secops-api';

type SignalRHandler = (data: unknown) => void;

interface SignalRState {
  connection: HubConnection | null;
  listeners: Map<string, Set<SignalRHandler>>;
  connecting: boolean;
}

// Singleton — shared across all components
const state: SignalRState = {
  connection: null,
  listeners: new Map(),
  connecting: false,
};

async function ensureConnection(): Promise<HubConnection | null> {
  if (state.connection?.state === HubConnectionState.Connected) {
    return state.connection;
  }

  if (state.connecting) return null;
  state.connecting = true;

  try {
    const result = await secopsApi.post<{ url?: string; accessToken?: string; disabled?: boolean }>('/secops/signalr/negotiate');
    if (result.disabled || !result.url || !result.accessToken) {
      state.connecting = false;
      return null;
    }
    const { url, accessToken } = result;

    const connection = new HubConnectionBuilder()
      .withUrl(url, { accessTokenFactory: () => accessToken })
      .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
      .configureLogging(LogLevel.Warning)
      .build();

    // Re-register all active listeners on the connection
    for (const [event, handlers] of state.listeners) {
      connection.on(event, (data: unknown) => {
        for (const handler of handlers) {
          try { handler(data); } catch { /* ignore handler errors */ }
        }
      });
    }

    await connection.start();
    state.connection = connection;
    return connection;
  } catch (err) {
    console.warn('[SignalR] Connection failed, polling will continue:', err instanceof Error ? err.message : err);
    return null;
  } finally {
    state.connecting = false;
  }
}

/**
 * Subscribe to a SignalR event. Returns an unsubscribe function.
 * Automatically connects on first subscription.
 */
function subscribe(event: string, handler: SignalRHandler): () => void {
  if (!state.listeners.has(event)) {
    state.listeners.set(event, new Set());
  }
  state.listeners.get(event)!.add(handler);

  // If already connected, add handler directly
  if (state.connection?.state === HubConnectionState.Connected) {
    state.connection.on(event, handler);
  }

  // Ensure connection exists
  ensureConnection();

  return () => {
    state.listeners.get(event)?.delete(handler);
    if (state.connection?.state === HubConnectionState.Connected) {
      state.connection.off(event, handler);
    }
  };
}

/**
 * Hook to listen for a specific SignalR event.
 * Calls the callback whenever the event fires.
 * Safe to use in multiple components — shares a single connection.
 */
export function useSignalREvent(event: string, callback: SignalRHandler): void {
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  const stableHandler = useCallback((data: unknown) => {
    callbackRef.current(data);
  }, []);

  useEffect(() => {
    return subscribe(event, stableHandler);
  }, [event, stableHandler]);
}

/**
 * Hook that returns a refresh trigger that fires on a specific SignalR event.
 * Use with useSecOpsPolling's refresh() — triggers an immediate data refetch
 * when a real-time event arrives, while still keeping polling as a fallback.
 */
export function useSignalRRefresh(event: string, refresh: () => void): void {
  useSignalREvent(event, refresh);
}
